import { drawnImageUrl } from 'fym-shared/src/images/drawnImageUrl';
import { isProxiableRemoteImage } from 'fym-shared/src/images/imgproxySources';
import type { SerializedImageSourceFile } from 'fym-shared/src/music24/imageSourceFile';
import { config } from '../../config/config';
import { getFullAPIFileURL } from './getFullAPIFileURL';

/**
 * Owner rule 2026-10-19: every image that the app draws goes through our imgproxy, sized for the
 * place where it is drawn. The `files/<uuid>/download` route streams the original file (a cover
 * can be 2.5 MB for a 40 px tile), so it is for downloads, video and audio only.
 *
 * The API sends only the imgproxy source of a file (`sourceUrl`). These helpers build the URL for
 * the CSS size of the place, times the device pixel ratio (`drawnImageUrl` in fym-shared).
 */

function devicePixelRatio(): number {
  return typeof window === 'undefined' ? 1 : window.devicePixelRatio;
}

/** The imgproxy URL of an absolute source, or null when the app has no imgproxy. */
export function imgproxySrc(sourceUrl: string, cssPx: number, options: { square?: boolean } = {}): string | null {
  const base = config.imageProcessingURL;
  if (base === undefined || base === null || base === '') {
    return null;
  }
  return drawnImageUrl({
    imgproxyBase: base,
    sourceUrl,
    cssPx,
    devicePixelRatio: devicePixelRatio(),
    square: options.square,
  });
}

/**
 * A stored file as an `<img>` source. With `sourceUrl` (every API since 2026-10-19) it goes
 * through imgproxy; an older API answer falls back to the file route.
 */
export function drawnFileSrc(
  file: Pick<SerializedImageSourceFile, 'url' | 'sourceUrl'>,
  cssPx: number,
  options: { square?: boolean } = {}
): string {
  if (file.sourceUrl !== undefined && file.sourceUrl !== '') {
    const proxied = imgproxySrc(file.sourceUrl, cssPx, options);
    if (proxied !== null) {
      return proxied;
    }
  }
  return getFullAPIFileURL(file.url);
}

/**
 * An outside image URL (a streaming service cover) as an `<img>` source: through imgproxy when
 * its host is on the imgproxy allow list, else the URL as it is.
 */
export function drawnRemoteSrc(url: string, cssPx: number, options: { square?: boolean } = {}): string {
  if (!isProxiableRemoteImage(url)) {
    return url;
  }
  return imgproxySrc(url, cssPx, options) ?? url;
}
